<template>
    <div class="w-100 align-self-start">
        <v-card flat color="transparent">
            <v-card-title>
                <span class="secondary--text">Cloud accounts</span>
            </v-card-title>
            <v-card-text>
                <div class="d-flex align-center">
                    <v-btn color="primary" class="mr-2" :disabled="saving || actionInProgress || bootstrapping" @click="openBootstrapDialog">
                        <v-icon left>mdi-plus</v-icon>
                        Cloud account
                    </v-btn>

                    <v-spacer></v-spacer>

                    <v-text-field
                        v-model="search"
                        prepend-inner-icon="mdi-magnify "
                        autofocus
                        hide-details
                        solo
                        flat
                        dense
                        clearable
                        placeholder="Search for a cloud account..."
                        class="mr-2"></v-text-field>
                    <v-btn icon :loading="loading" @click="fetchCloudAccount">
                        <v-icon>refresh</v-icon>
                    </v-btn>
                </div>

                <v-alert v-if="error" type="error" text dismissible class="mt-4 mb-0" @input="error = ''">{{ error }}</v-alert>
                <v-data-table
                    :headers="headers"
                    :items="cloudAccounts"
                    :search="search"
                    :loading="loading"
                    hide-default-footer
                    class="mt-4"
                    no-data-text="No cloud account is connected to this resource pool.">
                    <template v-slot:[`item.credentials`]="{ item }">{{ item && item.cloud_credentials_srid ? 'Configured' : 'Not configured' }}</template>
                    <template v-slot:[`item.actions`]="{ item }">
                        <div class="text-right">
                            <v-menu left offset-x nudge-top="10">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn v-bind="attrs" v-on="on" text color="secondary" small :loading="actionInProgress"><v-icon>more_horiz</v-icon></v-btn>
                                </template>
                                <v-list nav dense>
                                    <v-list-item @click="openEditDialog(item)">
                                        <v-list-item-icon><v-icon>mdi-pencil-outline</v-icon></v-list-item-icon>
                                        <v-list-item-title class="mr-4">Edit</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item :disabled="!item.tcid" @click="reconcileCloudAccount(item)">
                                        <v-list-item-icon><v-icon>mdi-sync</v-icon></v-list-item-icon>
                                        <v-list-item-title class="mr-4">Reconcile</v-list-item-title>
                                    </v-list-item>
                                    <v-divider class="my-2"></v-divider>
                                    <v-list-item :disabled="!item.tcid" @click="confirmCloudAccountAction(item, 'unlink')">
                                        <v-list-item-icon><v-icon color="warning">mdi-link-off</v-icon></v-list-item-icon>
                                        <v-list-item-title class="warning--text mr-4">Unlink</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item :disabled="!item.tcid" @click="confirmCloudAccountAction(item, 'teardown')">
                                        <v-list-item-icon><v-icon color="error">mdi-trash-can-outline</v-icon></v-list-item-icon>
                                        <v-list-item-title class="error--text mr-4">Teardown</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </div>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <v-dialog v-model="cloudAccountDialog" max-width="640px" persistent>
            <v-card flat>
                <v-card-title class="flex-nowrap align-start">
                    <span class="secondary--text">Edit cloud account</span>
                    <v-spacer></v-spacer>
                    <v-btn icon :disabled="saving" @click="closeCloudAccountDialog"><v-icon>clear</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <v-form ref="cloudAccountForm" v-model="formValid" @submit.prevent="saveCloudAccount">
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-select
                                    v-model="cloudAccountForm.cloudProvider"
                                    :items="cloudProviderOptions"
                                    item-text="display_label"
                                    item-value="cloud_provider"
                                    label="Cloud provider"
                                    :rules="[required]"
                                    outlined
                                    dense
                                    :loading="loadingCloudProviders"
                                    :disabled="saving || loadingCloudProviders"
                                    no-data-text="No cloud providers available."></v-select>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-select
                                    v-model="cloudAccountForm.cloudRegion"
                                    :items="editCloudRegionOptions"
                                    label="Cloud region"
                                    :rules="[required]"
                                    outlined
                                    dense
                                    :loading="loadingCloudProviders"
                                    :disabled="saving || loadingCloudProviders || !cloudAccountForm.cloudProvider"
                                    no-data-text="No regions available."></v-select>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text :loading="saving" :disabled="!formValid" @click="saveCloudAccount">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="bootstrapDialog" max-width="640px" persistent>
            <v-card flat>
                <v-card-title class="flex-nowrap align-start">
                    <span class="secondary--text">Create cloud account</span>
                    <v-spacer></v-spacer>
                    <v-btn icon :disabled="bootstrapping" @click="closeBootstrapDialog"><v-icon>clear</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <v-form ref="bootstrapForm" v-model="bootstrapFormValid" @submit.prevent="bootstrapCloudCluster">
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-select
                                    v-model="bootstrapSettings.cloudProvider"
                                    :items="cloudProviderOptions"
                                    item-text="display_label"
                                    item-value="cloud_provider"
                                    label="Cloud provider"
                                    :rules="[required]"
                                    outlined
                                    dense
                                    :loading="loadingCloudProviders"
                                    :disabled="bootstrapping || loadingCloudProviders"
                                    no-data-text="No cloud providers available."></v-select>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-select
                                    v-model="bootstrapSettings.cloudRegion"
                                    :items="cloudRegionOptions"
                                    label="Cloud region"
                                    :rules="[required]"
                                    outlined
                                    dense
                                    :loading="loadingCloudProviders"
                                    :disabled="bootstrapping || loadingCloudProviders || !bootstrapSettings.cloudProvider"
                                    no-data-text="No regions available."></v-select>
                            </v-col>
                        </v-row>
                        <v-text-field
                            v-model="bootstrapCredentials.id"
                            label="OpenStack application credential ID"
                            :rules="[required]"
                            outlined
                            dense
                            :disabled="bootstrapping"></v-text-field>
                        <v-text-field
                            v-model="bootstrapCredentials.secret"
                            label="OpenStack application credential secret"
                            :rules="[required]"
                            type="password"
                            outlined
                            dense
                            :disabled="bootstrapping"></v-text-field>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text :loading="bootstrapping" :disabled="!bootstrapFormValid" @click="bootstrapCloudCluster">Bootstrap</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import eventBus from '@/components/EventBus'

export default {
    name: 'CloudAccountsPage',
    data() {
        return {
            search: '',
            loading: false,
            loadingCloudProviders: false,
            saving: false,
            bootstrapping: false,
            actionInProgress: false,
            pendingCloudAccountAction: null,
            globalDialogHash: `cloud-account-action-${this._uid}`,
            error: '',
            cloudAccounts: [],
            cloudAccountDialog: false,
            bootstrapDialog: false,
            editingCloudAccount: null,
            formValid: false,
            bootstrapFormValid: false,
            cloudProviders: [],
            bootstrapSettings: {
                cloudProvider: null,
                cloudRegion: null
            },
            bootstrapCredentials: {
                id: '',
                secret: ''
            },
            cloudAccountForm: this.emptyCloudAccountForm(),
            headers: [
                { text: 'Provider', value: 'cloud_provider' },
                { text: 'Region', value: 'cloud_region' },
                { text: 'Cluster', value: 'cluster_name' },
                { text: 'Credentials', value: 'credentials', sortable: false },
                { text: '', value: 'actions', align: 'right', sortable: false }
            ]
        }
    },
    computed: {
        resourcePoolId() {
            return Number(this.$route.params.rpid)
        },
        cloudProviderOptions() {
            return this.cloudProviders
        },
        cloudRegionOptions() {
            if (!this.bootstrapSettings.cloudProvider) {
                return []
            }
            const selectedProvider = this.cloudProviders.find(provider => provider.cloud_provider === this.bootstrapSettings.cloudProvider)
            return selectedProvider?.regions || []
        },
        editCloudRegionOptions() {
            if (!this.cloudAccountForm.cloudProvider) {
                return []
            }
            const selectedProvider = this.cloudProviders.find(provider => provider.cloud_provider === this.cloudAccountForm.cloudProvider)
            return selectedProvider?.regions || []
        }
    },
    created() {
        eventBus.$on(`global-dialog-accept-${this.globalDialogHash}`, this.executePendingCloudAccountAction)
    },
    beforeDestroy() {
        eventBus.$off(`global-dialog-accept-${this.globalDialogHash}`, this.executePendingCloudAccountAction)
    },
    methods: {
        emptyCloudAccountForm() {
            return {
                cloudProvider: '',
                cloudRegion: ''
            }
        },
        required(value) {
            return Boolean(String(value || '').trim()) || 'Required'
        },
        notify(text, icon = 'check_circle') {
            this.$store.dispatch('showSnackBar', { snackBarText: text, snackBarTimeout: 5000, snackBarIcon: icon })
        },
        normalizeCloudProviders(cloudProvidersResponse) {
            if (Array.isArray(cloudProvidersResponse)) {
                return cloudProvidersResponse.map(provider => ({
                    cloud_provider: provider.cloud_provider,
                    display_label: provider.display_label || provider.cloud_provider,
                    regions: Array.isArray(provider.regions) ? provider.regions : []
                }))
            }

            if (cloudProvidersResponse && typeof cloudProvidersResponse === 'object') {
                return Object.entries(cloudProvidersResponse).map(([cloudProvider, regions]) => ({
                    cloud_provider: cloudProvider,
                    display_label: cloudProvider,
                    regions: Array.isArray(regions) ? regions : []
                }))
            }

            return []
        },
        normalizeTenantClusters(tenantClusterResponse) {
            if (Array.isArray(tenantClusterResponse)) {
                return tenantClusterResponse
            }

            if (tenantClusterResponse && typeof tenantClusterResponse === 'object') {
                return [tenantClusterResponse]
            }

            return []
        },
        async fetchCloudProviders() {
            this.loadingCloudProviders = true
            try {
                const { data } = await this.$axios.get('/tenant_clusters/cloud_providers')
                this.cloudProviders = this.normalizeCloudProviders(data?.cloud_providers)
            } catch (error) {
                this.cloudProviders = []
                this.error = error.response?.data?.debug || 'Could not load cloud providers.'
            } finally {
                this.loadingCloudProviders = false
            }
        },
        async fetchCloudAccount() {
            if (!this.resourcePoolId) return
            this.loading = true
            this.error = ''
            try {
                const { data } = await this.$axios.get(`/resource_pools/${this.resourcePoolId}/tenant_cluster`)
                this.cloudAccounts = this.normalizeTenantClusters(data)
            } catch (error) {
                if (error.response?.status === 404) {
                    this.cloudAccounts = []
                    return
                }
                this.error = error.response?.data?.debug || 'Could not load cloud account.'
            } finally {
                this.loading = false
            }
        },
        openEditDialog(cloudAccount) {
            this.editingCloudAccount = cloudAccount
            this.cloudAccountForm = {
                cloudProvider: cloudAccount.cloud_provider || '',
                cloudRegion: cloudAccount.cloud_region || ''
            }
            this.fetchCloudProviders()
            this.cloudAccountDialog = true
        },
        openBootstrapDialog() {
            this.bootstrapSettings = {
                cloudProvider: null,
                cloudRegion: null
            }
            this.bootstrapCredentials = { id: '', secret: '' }
            this.bootstrapDialog = true
            this.fetchCloudProviders()
        },
        closeCloudAccountDialog() {
            this.cloudAccountDialog = false
            this.editingCloudAccount = null
            this.$refs.cloudAccountForm?.resetValidation()
        },
        closeBootstrapDialog() {
            this.bootstrapDialog = false
            this.$refs.bootstrapForm?.resetValidation()
        },
        async saveCloudAccount() {
            if (!this.$refs.cloudAccountForm || !this.$refs.cloudAccountForm.validate()) return
            if (!this.editingCloudAccount) {
                this.error = 'No cloud account selected for editing.'
                return
            }
            this.saving = true
            this.error = ''
            try {
                const payload = {
                    cloud_provider: this.cloudAccountForm.cloudProvider.trim(),
                    cloud_region: this.cloudAccountForm.cloudRegion.trim()
                }
                await this.$axios.patch(`/resource_pools/${this.resourcePoolId}/tenant_cluster`, payload)
                this.closeCloudAccountDialog()
                this.notify('Cloud account updated.')
                await this.fetchCloudAccount()
            } catch (error) {
                this.error = error.response?.data?.debug || 'Could not save cloud account.'
            } finally {
                this.saving = false
            }
        },
        async bootstrapCloudCluster() {
            if (!this.$refs.bootstrapForm || !this.$refs.bootstrapForm.validate()) return
            this.bootstrapping = true
            this.error = ''
            try {
                await this.$axios.post(`/resource_pools/${this.resourcePoolId}/tenant_clusters`, {
                    cloud_provider: this.bootstrapSettings.cloudProvider,
                    cloud_region: this.bootstrapSettings.cloudRegion,
                    os_app_credential_id: this.bootstrapCredentials.id.trim(),
                    os_app_credential_secret: this.bootstrapCredentials.secret
                })
                this.closeBootstrapDialog()
                this.notify('Cluster bootstrap has started.')
                await this.fetchCloudAccount()
            } catch (error) {
                this.error = error.response?.data?.debug || 'Could not start cluster bootstrap.'
            } finally {
                this.bootstrapping = false
            }
        },
        reconcileCloudAccount(cloudAccount) {
            return this.performCloudAccountAction(cloudAccount, 'reconcile', 'post', 'Cloud account reconciliation has started.')
        },
        confirmCloudAccountAction(cloudAccount, action) {
            const actionLabel = action === 'unlink' ? 'unlink' : 'teardown'
            this.pendingCloudAccountAction = { cloudAccount, action: actionLabel, method: actionLabel === 'teardown' ? 'delete' : 'post' }
            this.$store.dispatch('showGlobalDialog', {
                dialogTitle: `${actionLabel[0].toUpperCase()}${actionLabel.slice(1)} cloud account`,
                dialogText: `Are you sure you want to ${actionLabel} this cloud account?`,
                dialogAction: ['accept', 'dismiss'],
                hash: this.globalDialogHash
            })
        },
        executePendingCloudAccountAction() {
            if (!this.pendingCloudAccountAction) return
            const { cloudAccount, action, method } = this.pendingCloudAccountAction
            this.pendingCloudAccountAction = null
            return this.performCloudAccountAction(cloudAccount, action, method)
        },
        async performCloudAccountAction(cloudAccount, action, method, successMessage) {
            if (!cloudAccount.tcid) return
            this.actionInProgress = true
            this.error = ''
            try {
                const suffix = action === 'reconcile' || action === 'unlink' ? `/${action}` : ''
                await this.$axios[method](`/resource_pools/${this.resourcePoolId}/tenant_clusters/${cloudAccount.tcid}${suffix}`)
                this.notify(successMessage || `Cloud account ${action} has started.`)
                await this.fetchCloudAccount()
            } catch (error) {
                this.error = error.response?.data?.debug || `Could not ${action} cloud account.`
            } finally {
                this.actionInProgress = false
            }
        }
    },
    watch: {
        resourcePoolId: {
            immediate: true,
            handler() {
                this.fetchCloudAccount()
            }
        },
        'bootstrapSettings.cloudProvider'(newCloudProvider) {
            if (!newCloudProvider) {
                this.bootstrapSettings.cloudRegion = null
                return
            }
            if (!this.cloudRegionOptions.includes(this.bootstrapSettings.cloudRegion)) {
                this.bootstrapSettings.cloudRegion = null
            }
        },
        'cloudAccountForm.cloudProvider'(newCloudProvider) {
            if (!newCloudProvider) {
                this.cloudAccountForm.cloudRegion = ''
                return
            }
            if (!this.editCloudRegionOptions.includes(this.cloudAccountForm.cloudRegion)) {
                this.cloudAccountForm.cloudRegion = ''
            }
        }
    }
}
</script>
